/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import { StatusLight } from '@adobe/react-spectrum';
import {
    Breadcrumbs,
    Inventory,
    PageHeader,
    ViewHeader,
    Counter,
    TableView,
    Row,
    Cell,
    FolderRail,
    FolderTree,
    Pagination,
    Search
} from '@quarry/inventory';

import { useSelectionContext } from '../common/SelectionContext';
import ThumbnailCell from '../common/ThumbnailCell';
import { useOverlayFix } from '../common/useOverlayFix';
import { useCategoryPickerContext } from './CategoryPickerContext';
import { trackEvent } from '../../utils/tracking';
import { event_categorypicker_loaded } from '../../utils/trackingEvents';
import { useGraphqlSchemaContext } from '../GraphqlSchemaContext/GraphqlSchemaContext';

// i18n
const messages = defineMessages({
    rootCategory: {
        id: 'navigation.rootCategory',
        defaultMessage: 'Root Category'
    },
    columnName: {
        id: 'column.name',
        defaultMessage: 'Name'
    },
    columnID: {
        id: 'column.id',
        defaultMessage: 'ID'
    },
    columnUID: {
        id: 'column.uid',
        defaultMessage: 'UID'
    },
    columnUrlPath: {
        id: 'column.urlPath',
        defaultMessage: 'URL Path'
    },
    columnUrlKey: {
        id: 'column.urlKey',
        defaultMessage: 'URL Key'
    },
    statusStagedData: {
        id: 'status.stagedData',
        defaultMessage: 'Staged Data'
    }
});

const renderCells = item => {
    return (
        <Row key={item.key}>
            {columnKey => {
                if (columnKey === 'name') {
                    return (
                        <Cell>
                            <ThumbnailCell thumbnail={item['image']} thumbnailAlt={item[columnKey]}>
                                {item[columnKey]}
                            </ThumbnailCell>
                        </Cell>
                    );
                } else {
                    return <Cell>{item[columnKey]}</Cell>;
                }
            }}
        </Row>
    );
};

const CategoryPicker = props => {
    const { multiple } = props;

    const ref = useRef();
    useOverlayFix(ref);
    const [{ pickerSelection }, { setPickerSelection }] = useSelectionContext();
    const [
        { items, path, loadingState, pagination, paginationSupported, filterState },
        { onFolderExpand, onFilterChange }
    ] = useCategoryPickerContext();
    const { formatMessage } = useIntl();
    const [, , [config]] = useGraphqlSchemaContext();
    const displayName = config.name ? config.name : formatMessage(messages.rootCategory);

    const columns = [
        {
            key: 'name',
            label: formatMessage(messages.columnName)
        },
        {
            key: config.supportUID ? 'uid' : 'id',
            label: config.supportUID ? formatMessage(messages.columnUID) : formatMessage(messages.columnID)
        },
        {
            key: 'url_key',
            label: formatMessage(messages.columnUrlKey)
        },
        {
            key: 'url_path',
            label: formatMessage(messages.columnUrlPath)
        }
    ];

    useEffect(() => {
        trackEvent(event_categorypicker_loaded);
    }, []);

    return (
        <div data-test-id="category-picker">
            <span ref={ref} />
            <Inventory
                height="95vh"
                selectionMode={multiple ? 'multiple' : 'single'}
                items={items}
                loadingState={loadingState}
                onFilterChange={onFilterChange}
                selectedKeys={pickerSelection}
                onSelectionChange={setPickerSelection}
                defaultSelectedFilters={filterState}
                {...(paginationSupported ? { pagination } : {})}
            >
                <Search wait={200} />
                <FolderRail defaultOpen>
                    <FolderTree rootFolder={displayName} path={path} onFolderExpand={onFolderExpand} />
                </FolderRail>
                <PageHeader>
                    <Breadcrumbs path={path} />
                    {config.previewDate && (
                        <StatusLight variant="notice">{formatMessage(messages.statusStagedData)}</StatusLight>
                    )}
                </PageHeader>
                {paginationSupported && (
                    <ViewHeader>
                        <Counter />
                    </ViewHeader>
                )}
                <TableView columns={columns}>{renderCells}</TableView>
                {paginationSupported && <Pagination />}
            </Inventory>
        </div>
    );
};

CategoryPicker.propTypes = {
    multiple: PropTypes.bool
};

export default CategoryPicker;
